import { z } from 'zod';
import { PaginationOffsetHasMoreSchema } from './shared/pagination.js';

export const FRIENDIMIZER_SORT_FIELDS = [
  'influenceScore',
  'tweetsPerWeek',
  'daysSinceLastTweet',
  'engagementRate',
] as const;
export type FriendimizerSortField = (typeof FRIENDIMIZER_SORT_FIELDS)[number];

export const FriendimizerAffiliationSchema = z
  .object({
    organizationProfileId: z.string(),
    organizationName: z.string().optional(),
    organizationUsername: z.string().optional(),
    badgeUrl: z.string().url().nullable().optional(),
    url: z.string().url().nullable().optional(),
  })
  .strict();

export const FriendimizerRelationshipSchema = z
  .object({
    followedSince: z.date().nullable(),
    lastSeenAt: z.date().nullable(),
    followsYou: z.boolean(),
  })
  .strict();

export const FriendimizerUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().url().nullable().optional(),
  verifiedType: z.enum(['none', 'blue', 'business', 'government']).default('none'),
  followersCount: z.number().optional(),
  followingCount: z.number().optional(),
  lastTweetDate: z.date().nullable().optional(),
  engagementScore: z.number().optional(),
  unfollowRecommendation: z
    .object({
      score: z.number(),
      reasons: z.array(z.string()),
    })
    .optional(),
  bio: z.string().nullable().optional(),
  tweetCount: z.number().optional(),
  listedCount: z.number().optional(),
  tweetsPerWeek: z.number().optional(),
  daysSinceLastTweet: z.number().nullable().optional(),
  engagementRate: z.number().optional(),
  influenceScore: z.number().optional(),
  isInactive: z.boolean().optional(),
  followsBack: z.boolean().default(false).optional(),
  isBusinessAccount: z.boolean().default(false).optional(),
  lastTweetAt: z.date().optional(),
  mutualFollowersCount: z.number().optional(),
  isActive: z.boolean().default(true),
  category: z.string().optional(),
  lastTweetId: z.string().nullable().optional(),
  lastTweetText: z.string().nullable().optional(),
  affiliations: z.array(FriendimizerAffiliationSchema).optional(),
  primaryAffiliation: FriendimizerAffiliationSchema.nullable().optional(),
  relationship: FriendimizerRelationshipSchema.optional(),
  replyInbound: z.number().optional(),
  replyOutbound: z.number().optional(),
  retweetInbound: z.number().optional(),
  retweetOutbound: z.number().optional(),
  mentionInbound: z.number().optional(),
  mentionOutbound: z.number().optional(),
  interactionScore: z.number().optional(),
  interactionReciprocity: z.number().nullable().optional(),
  lastOutboundInteractionAt: z.date().nullable().optional(),
  lastInboundInteractionAt: z.date().nullable().optional(),
});

export type FriendimizerUser = z.infer<typeof FriendimizerUserSchema>;

export const FriendimizerImpactPaginationSchema = PaginationOffsetHasMoreSchema.extend({
  limit: PaginationOffsetHasMoreSchema.shape.limit.min(0),
  offset: PaginationOffsetHasMoreSchema.shape.offset.min(0),
  totalCount: z.number().int().nonnegative(),
});

export const FriendimizerImpactResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(FriendimizerUserSchema),
  pagination: FriendimizerImpactPaginationSchema,
  isIncomplete: z.boolean().optional(),
  syncDisabledNote: z.string().nullable().optional(),
});

export type FriendimizerImpactResponse = z.infer<typeof FriendimizerImpactResponseSchema>;

export const FriendimizerImpactQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional(),
  sortBy: z.enum(FRIENDIMIZER_SORT_FIELDS).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  hideInactive: z.coerce.boolean().optional(),
  hideVerified: z.coerce.boolean().optional(),
  influenceScoreMin: z.coerce.number().min(0).max(100).optional(),
  influenceScoreMax: z.coerce.number().min(0).max(100).optional(),
});

export type FriendimizerImpactQuery = z.infer<typeof FriendimizerImpactQuerySchema>;

export const FriendimizerFiltersSchema = z
  .object({
    sortBy: z.enum(FRIENDIMIZER_SORT_FIELDS).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    hideInactive: z.boolean().optional(),
    hideVerified: z.boolean().optional(),
    influenceScoreMin: z.number().min(0).max(100).optional(),
    influenceScoreMax: z.number().min(0).max(100).optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .strict();

export type FriendimizerFilters = z.infer<typeof FriendimizerFiltersSchema>;

// ============================================================================
// Additional Response Schemas
// ============================================================================

export const FriendimizerDataResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(FriendimizerUserSchema),
    isIncomplete: z.boolean().optional(),
    syncDisabledNote: z.string().optional(),
  }),
});

export type FriendimizerDataResponse = z.infer<typeof FriendimizerDataResponseSchema>;

export const FriendimizerRecommendationsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: z.array(FriendimizerUserSchema),
  }),
});
export type FriendimizerRecommendationsResponse = z.infer<typeof FriendimizerRecommendationsResponseSchema>;

export const FriendimizerStatsSchema = z.object({
  totalFollowing: z.number(),
  totalFollowers: z.number(),
  inactive: z.number(),
  verified: z.number(),
  nonMutuals: z.number(),
  isIncomplete: z.boolean().optional(),
  lastSyncAt: z.date().optional(),
});

export type FriendimizerStats = z.infer<typeof FriendimizerStatsSchema>;

export const FriendimizerStatsResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    data: FriendimizerStatsSchema,
    isIncomplete: z.boolean().optional(),
  }),
});
export type FriendimizerStatsResponse = z.infer<typeof FriendimizerStatsResponseSchema>;

export const FriendimizerSuccessResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  note: z.string().optional(),
  jobId: z.string().optional(),
});
export type FriendimizerSuccessResponse = z.infer<typeof FriendimizerSuccessResponseSchema>;

export const FriendimizerUnfollowBodySchema = z.object({
  action: z.literal('unfollow'),
  userId: z.string().regex(/^[0-9]+$/, 'Profile ID must be a numeric Twitter identifier'),
});
export type FriendimizerUnfollowBody = z.infer<typeof FriendimizerUnfollowBodySchema>;

export const FriendimizerUnfollowInputSchema = FriendimizerUnfollowBodySchema.omit({ action: true });
export type FriendimizerUnfollowInput = z.infer<typeof FriendimizerUnfollowInputSchema>;

export const FriendimizerBatchUnfollowBodySchema = z.object({
  action: z.literal('batch-unfollow'),
  userIds: z
    .array(z.string().regex(/^[0-9]+$/))
    .min(1)
    .max(50),
});
export type FriendimizerBatchUnfollowBody = z.infer<typeof FriendimizerBatchUnfollowBodySchema>;

export const FriendimizerBatchUnfollowInputSchema = FriendimizerBatchUnfollowBodySchema.omit({ action: true });
export type FriendimizerBatchUnfollowInput = z.infer<typeof FriendimizerBatchUnfollowInputSchema>;

export const FriendimizerBatchUnfollowResultSchema = z.object({
  success: z.array(z.string()),
  failed: z.array(
    z.object({
      userId: z.string(),
      error: z.string(),
    })
  ),
});
export type FriendimizerBatchUnfollowResult = z.infer<typeof FriendimizerBatchUnfollowResultSchema>;

export const FriendimizerFollowingListQuerySchema = z.object({
  action: z.literal('following-list'),
  influenceScoreMin: z.coerce.number().optional(),
  influenceScoreMax: z.coerce.number().optional(),
  hideInactive: z.coerce.boolean().optional(),
  hideVerified: z.coerce.boolean().optional(),
  sortBy: z.enum(FRIENDIMIZER_SORT_FIELDS).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type FriendimizerFollowingListQuery = z.infer<typeof FriendimizerFollowingListQuerySchema>;

export const FriendimizerRecommendationsQuerySchema = z.object({
  action: z.literal('recommendations'),
  limit: z.coerce.number().min(1).max(100).default(10).optional(),
});
export type FriendimizerRecommendationsQuery = z.infer<typeof FriendimizerRecommendationsQuerySchema>;

export const FriendimizerStatisticsQuerySchema = z.object({
  action: z.literal('statistics'),
});
export type FriendimizerStatisticsQuery = z.infer<typeof FriendimizerStatisticsQuerySchema>;

export const FriendimizerUpdateFollowingQuerySchema = z.object({
  action: z.literal('update-following'),
});
export type FriendimizerUpdateFollowingQuery = z.infer<typeof FriendimizerUpdateFollowingQuerySchema>;

export const FriendimizerUpdateFollowersQuerySchema = z.object({
  action: z.literal('update-followers'),
});
export type FriendimizerUpdateFollowersQuery = z.infer<typeof FriendimizerUpdateFollowersQuerySchema>;

export const FriendimizerUpdateProfilesQuerySchema = z.object({
  action: z.literal('update-profiles'),
});
export type FriendimizerUpdateProfilesQuery = z.infer<typeof FriendimizerUpdateProfilesQuerySchema>;

export const FriendimizerBatchUnfollowResponseSchema = z.object({
  success: z.boolean(),
  data: FriendimizerBatchUnfollowResultSchema,
});
export type FriendimizerBatchUnfollowResponse = z.infer<typeof FriendimizerBatchUnfollowResponseSchema>;

// ============================================================================
